<template>
    <div class="to-personal-stats">
        <!-- Заголовок и описание системы -->
        <div v-if="!hideMotivationText" class="motivation-header mb-4">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">Система мотивации отдела ТО</h5>
                    <p class="card-text">
                        Ваша работа — это не просто оформление деклараций. Вы — ключевые «добытчики» прибыли и роста для компании. 
                        Новая система мотивации построена на простом и честном принципе: 
                        <strong>«Чем больше прибыли вы приносите компании, тем выше ваше вознаграждение»</strong>.
                    </p>
                    <p class="card-text">
                        Ваш доход теперь напрямую зависит от трех ключевых результатов:
                    </p>
                    <ul>
                        <li><strong>Объем вашей работы</strong> — сколько деклараций вы оформили</li>
                        <li><strong>Качество вашей работы</strong> — какую маржинальность (прибыль) вы сгенерировали</li>
                        <li><strong>Развитие бизнеса</strong> — сколько новых клиентов вы привели</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Лоадер -->
        <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Загрузка...</span>
            </div>
            <p class="mt-2">Загрузка персональной статистики...</p>
        </div>

        <!-- Персональная статистика -->
        <div v-else>

            <!-- Расчет бонуса -->
            <div class="bonus-calculation mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">
                            <template v-if="!hideMotivationText">Расчет вашего бонуса</template>
                            <template v-else>Расчет бонуса</template>
                        </h6>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-4">
                                <div class="calculation-step">
                                    <h6>Шаг 1: План выполнения</h6>
                                    <p class="text-muted">Декларации / План (90 деклараций)</p>
                                    <div class="calculation-result">
                                        <span class="h4 font-weight-bold">{{ personalStats.declarations }} / 90 = {{ Math.round((personalStats.declarations / 90) * 100) }}%</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="calculation-step">
                                    <h6>Шаг 2: Маржинальность</h6>
                                    <p class="text-muted">Сумма прибыли от вашей работы</p>
                                    <div class="calculation-result">
                                        <span class="h4 text-success font-weight-bold">{{ formatCurrency(personalStats.margin) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="calculation-step">
                                    <h6>Шаг 3: Сверхдоход</h6>
                                    <p class="text-muted">Маржа − ЗП (и прочие затраты компании)</p>
                                    <div class="calculation-result">
                                        <span class="h4 text-info font-weight-bold">{{ formatCurrency(personalStats.superIncome) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr>
                        <div class="final-bonus text-center">
                            <h5>Рассчитанный бонус: <span class="text-success h3 font-weight-bold">{{ formatCurrency(personalStats.bonus) }}</span></h5>
                            <div class="bonus-breakdown">
                                <p class="text-muted mb-1">
                                    <strong>Бонус по выполнению плана:</strong> {{ formatCurrency(personalStats.planBonus) }} 
                                    (Сверхдоход × {{ personalStats.bonusPercentage }}%)
                                </p>
                                <p class="text-muted mb-1">
                                    <strong>Бонус за новых клиентов:</strong> {{ formatCurrency(personalStats.newClientsBonus) }} 
                                    (20% от маржи новых клиентов)
                                </p>
                                <p v-if="personalStats.head_department_info && personalStats.head_department_info.is_head_manager" class="text-muted mb-1">
                                    <strong>Бонус руководителя от отдела:</strong> {{ formatCurrency(personalStats.headDepartmentBonus) }} 
                                    ({{ personalStats.head_department_info.head_department_percentage }}% от сверхрезультата отдела)
                                </p>
                                <hr class="my-2">
                                <p class="text-success font-weight-bold mb-0">
                                    <strong>Итого бонус:</strong> {{ formatCurrency(personalStats.bonus) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Информация о бонусе руководителя отдела (компактная версия) -->
            <div 
                v-if="personalStats.head_department_info && personalStats.head_department_info.is_head_manager" 
                class="mt-3 mb-4"
            >
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Детали бонуса руководителя</h6>
                    </div>
                    <div class="card-body">
                        <div class="row text-center">
                            <div class="col-md-3">
                                <small class="text-muted">Общая маржинальность отдела</small>
                                <p class="mb-0 font-weight-bold">{{ formatCurrency(personalStats.head_department_info.department_total_margin) }}</p>
                            </div>
                            <div class="col-md-3">
                                <small class="text-muted">План отдела</small>
                                <p class="mb-0 font-weight-bold">{{ formatCurrency(personalStats.head_department_info.department_plan) }}</p>
                            </div>
                            <div class="col-md-3">
                                <small class="text-muted">Сверхрезультат отдела</small>
                                <p class="mb-0 font-weight-bold text-success">{{ formatCurrency(personalStats.head_department_info.department_super_result) }}</p>
                            </div>
                            <div class="col-md-3">
                                <small class="text-muted">Бонус руководителя</small>
                                <p class="mb-0 font-weight-bold text-success">{{ formatCurrency(personalStats.headDepartmentBonus) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Список деклараций за текущий квартал -->
            <div class="declarations-list mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Ваши декларации за {{ getCurrentQuarterName() }}</h6>
                    </div>
                    <div class="card-body">
                        <!-- Лоадер для списка деклараций -->
                        <div v-if="declarationsLoading" class="text-center py-3">
                            <div class="spinner-border spinner-border-sm text-primary" role="status">
                                <span class="sr-only">Загрузка...</span>
                            </div>
                            <span class="ml-2">Загрузка деклараций...</span>
                        </div>

                        <!-- Таблица деклараций -->
                        <div v-else-if="declarationsData.length > 0">
                            <b-table
                                :items="declarationsData"
                                :fields="declarationsTableFields"
                                :per-page="declarationsPerPage"
                                :current-page="declarationsCurrentPage"
                                striped
                                hover
                                responsive
                                small
                                class="mb-3"
                            >
                                <template #cell(order_id)="row">
                                    <span 
                                        class="sidebar-link text-primary font-weight-bold cursor-pointer" 
                                        @click="openInNewWindow ? openOrderInNewWindow(row.item.order_id) : openOrderSidebar(row.item.order_id, '')" 
                                        :title="openInNewWindow ? 'Открыть заказ в новой вкладке' : 'Открыть заказ'"
                                    >
                                        #{{ row.item.order_id }}
                                    </span>
                                </template>
                                <template #cell(dt_numbers)="row">
                                    <div v-if="row.item.dt_numbers && row.item.dt_numbers.length > 0">
                                        <div 
                                            v-for="(dt, index) in row.item.dt_numbers" :key="index" 
                                        >
                                            {{ dt }}
                                        </div>
                                    </div>
                                    <span v-else class="text-muted">-</span>
                                </template>
                            </b-table>

                            <!-- Пагинация для деклараций -->
                            <div class="d-flex justify-content-between align-items-center mt-3">
                                <div class="d-flex align-items-center">
                                    <span class="text-muted mr-2">Показать:</span>
                                    <b-form-select
                                        v-model="declarationsPerPage"
                                        :options="declarationsPerPageOptions"
                                        size="sm"
                                        style="width: 80px;"
                                        class="mr-3"
                                    ></b-form-select>
                                    <span class="text-muted">
                                        {{ (declarationsCurrentPage - 1) * declarationsPerPage + 1 }}-{{ Math.min(declarationsCurrentPage * declarationsPerPage, declarationsData.length) }} 
                                        из {{ declarationsData.length }}
                                    </span>
                                </div>
                                
                                <b-pagination
                                    v-model="declarationsCurrentPage"
                                    :total-rows="declarationsData.length"
                                    :per-page="declarationsPerPage"
                                    size="sm"
                                    class="mb-0"
                                ></b-pagination>
                            </div>

                            <!-- Итоги -->
                            <b-alert variant="info" show class="py-2 mt-3">
                                <div class="d-flex justify-content-between align-items-center">
                                    <span class="font-weight-bold">Итоги за {{ getCurrentQuarterName() }}:</span>
                                    <span class="font-weight-bold">
                                        {{ declarationsSummary.total_orders }} поставок, 
                                        {{ declarationsSummary.total_dt_count }} деклараций
                                    </span>
                                </div>
                            </b-alert>
                        </div>

                        <!-- Сообщение, если нет деклараций -->
                        <div v-else class="text-center py-4">
                            <i class="fas fa-file-alt fa-3x mb-3 text-muted"></i>
                            <h6 class="text-muted">Нет деклараций за {{ getCurrentQuarterName() }}</h6>
                            <p class="text-muted">Начните оформлять декларации, чтобы увидеть их здесь</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Расчет маржинальности -->
            <div class="marginality-calculation-table mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Расчет маржинальности</h6>
                    </div>
                    <div class="card-body">
                        <!-- Лоадер для детальных расчетов -->
                        <div v-if="detailedCalculationsLoading" class="text-center py-3">
                            <div class="spinner-border spinner-border-sm text-primary" role="status">
                                <span class="sr-only">Загрузка...</span>
                            </div>
                            <p class="mt-2 mb-0 text-muted">Загрузка детальных расчетов...</p>
                        </div>
                        
                        <!-- Таблица детальных расчетов с пагинацией -->
                        <div v-else-if="detailedCalculations.length > 0">
                            <b-table
                                :items="detailedCalculations"
                                :fields="detailedCalculationsFields"
                                :per-page="perPage"
                                :current-page="currentPage"
                                striped
                                small
                                hover
                                responsive
                                :busy="detailedCalculationsLoading"
                                class="detailed-calculations-table"
                            >
                                <!-- Слот для заказа -->
                                <template #cell(order_id)="row">
                                    <span 
                                        class="sidebar-link text-primary font-weight-bold cursor-pointer" 
                                        @click="openInNewWindow ? openOrderInNewWindow(row.item.order_id) : openOrderSidebar(row.item.order_id, '')" 
                                        :title="openInNewWindow ? 'Открыть заказ в новой вкладке' : 'Открыть заказ'"
                                    >
                                        #{{ row.item.order_id }}
                                    </span>
                                </template>

                                <!-- Слот для названия клиента -->
                                <template #cell(company_name)="row">
                                    <span class="text-dark font-weight-medium">
                                        {{ row.item.company_name }}
                                    </span>
                                </template>

                                <!-- Слот для суммы клиента -->
                                <template #cell(client_amount)="row">
                                    <span class="text-secondary font-weight-bold">
                                        {{ formatCurrency(row.item.client_amount) }}
                                    </span>
                                </template>

                                <!-- Слот для суммы поставщика -->
                                <template #cell(supplier_amount)="row">
                                    <span class="text-primary font-weight-bold">
                                        {{ formatCurrency(row.item.supplier_amount) }}
                                    </span>
                                </template>

                                <!-- Слот для маржи -->
                                <template #cell(margin)="row">
                                    <span 
                                        class="font-weight-bold"
                                        :class="row.item.is_positive_margin ? 'text-success' : 'text-danger'"
                                    >
                                        {{ formatCurrency(row.item.margin) }}
                                    </span>
                                </template>

                                <!-- Слот для статуса -->
                                <template #cell(is_positive_margin)="row">
                                    <span 
                                        class="badge"
                                        :class="row.item.is_positive_margin ? 'badge-success' : (Number(row.item.margin) === 0 ? 'badge-secondary' : 'badge-danger')"
                                    >
                                        {{ row.item.is_positive_margin ? 'Прибыль' : (Number(row.item.margin) === 0 ? 'Нулевая маржа' : 'Убыток') }}
                                    </span>
                                </template>

                                <!-- Слот для даты расчета -->
                                <template #cell(calculated_at)="row">
                                    <small class="text-muted">
                                        {{ formatDate(row.item.calculated_at) }}
                                    </small>
                                </template>

                                <!-- Слот для загрузки -->
                                <template #table-busy>
                                    <div class="text-center text-primary my-2">
                                        <b-spinner class="align-middle"></b-spinner>
                                        <strong> Загрузка...</strong>
                                    </div>
                                </template>
                            </b-table>

                            <!-- Пагинация -->
                            <div class="d-flex justify-content-between align-items-center mt-3">
                                <div class="d-flex align-items-center">
                                    <span class="text-muted mr-2">Показать:</span>
                                    <b-form-select
                                        v-model="perPage"
                                        :options="perPageOptions"
                                        size="sm"
                                        style="width: 80px;"
                                        class="mr-3"
                                    ></b-form-select>
                                    <span class="text-muted">
                                        {{ (currentPage - 1) * perPage + 1 }}-{{ Math.min(currentPage * perPage, detailedCalculations.length) }} 
                                        из {{ detailedCalculations.length }}
                                    </span>
                                </div>
                                
                                <b-pagination
                                    v-model="currentPage"
                                    :total-rows="detailedCalculations.length"
                                    :per-page="perPage"
                                    size="sm"
                                    class="mb-0"
                                ></b-pagination>
                            </div>
                        </div>
                        
                        <!-- Сообщение если нет данных -->
                        <div v-else class="text-center py-4">
                            <i class="fas fa-chart-line fa-2x text-muted mb-2"></i>
                            <p class="text-muted mb-0">Нет данных о детальных расчетах за выбранный период</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Таблица новых клиентов -->
            <div class="new-clients-table mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Новые клиенты за {{ getCurrentQuarterName() }}</h6>
                    </div>
                    <div class="card-body">
                        <!-- Лоадер для новых клиентов -->
                        <div v-if="newClientsLoading" class="text-center py-3">
                            <div class="spinner-border spinner-border-sm text-primary" role="status">
                                <span class="sr-only">Загрузка...</span>
                            </div>
                            <p class="mt-2 mb-0 text-muted">Загрузка данных о новых клиентах...</p>
                        </div>
                        
                        <!-- Таблица новых клиентов с пагинацией -->
                        <div v-else-if="newClientsData.length > 0">
                            <b-table
                                :items="newClientsData"
                                :fields="newClientsTableFields"
                                :per-page="newClientsPerPage"
                                :current-page="newClientsCurrentPage"
                                striped
                                small
                                hover
                                responsive
                                :busy="newClientsLoading"
                                class="new-clients-table-content"
                            >
                                <!-- Слот для заказа -->
                                <template #cell(order_id)="row">
                                    <span 
                                        class="sidebar-link text-primary font-weight-bold cursor-pointer" 
                                        @click="openInNewWindow ? openOrderInNewWindow(row.item.order_id) : openOrderSidebar(row.item.order_id, '')" 
                                        :title="openInNewWindow ? 'Открыть заказ в новой вкладке' : 'Открыть заказ'"
                                    >
                                        #{{ row.item.order_id }}
                                    </span>
                                </template>

                                <!-- Слот для названия клиента -->
                                <template #cell(company_name)="row">
                                    <span class="text-dark font-weight-medium">
                                        {{ row.item.company_name }}
                                    </span>
                                </template>

                                <!-- Слот для суммы клиента -->
                                <template #cell(client_amount)="row">
                                    <span class="text-secondary font-weight-bold">
                                        {{ formatCurrency(row.item.client_amount) }}
                                    </span>
                                </template>

                                <!-- Слот для суммы поставщика -->
                                <template #cell(supplier_amount)="row">
                                    <span class="text-primary font-weight-bold">
                                        {{ formatCurrency(row.item.supplier_amount) }}
                                    </span>
                                </template>

                                <!-- Слот для маржи -->
                                <template #cell(margin)="row">
                                    <span 
                                        class="font-weight-bold"
                                        :class="row.item.is_positive_margin ? 'text-success' : 'text-danger'"
                                    >
                                        {{ formatCurrency(row.item.margin) }}
                                    </span>
                                </template>

                                <!-- Слот для бонуса -->
                                <template #cell(bonus)="row">
                                    <span class="text-success font-weight-bold">
                                        {{ formatCurrency(row.item.bonus) }}
                                    </span>
                                </template>

                                <!-- Слот для статуса -->
                                <template #cell(is_positive_margin)="row">
                                    <span 
                                        class="badge"
                                        :class="row.item.is_positive_margin ? 'badge-success' : (Number(row.item.margin) === 0 ? 'badge-secondary' : 'badge-danger')"
                                    >
                                        {{ row.item.is_positive_margin ? 'Прибыль' : (Number(row.item.margin) === 0 ? 'Нулевая маржа' : 'Убыток') }}
                                    </span>
                                </template>

                                <!-- Слот для даты расчета -->
                                <template #cell(calculated_at)="row">
                                    <small class="text-muted">
                                        {{ formatDate(row.item.calculated_at) }}
                                    </small>
                                </template>

                                <!-- Слот для загрузки -->
                                <template #table-busy>
                                    <div class="text-center text-primary my-2">
                                        <b-spinner class="align-middle"></b-spinner>
                                        <strong> Загрузка...</strong>
                                    </div>
                                </template>
                            </b-table>

                            <!-- Пагинация -->
                            <div class="d-flex justify-content-between align-items-center mt-3">
                                <div class="d-flex align-items-center">
                                    <span class="text-muted mr-2">Показать:</span>
                                    <b-form-select
                                        v-model="newClientsPerPage"
                                        :options="newClientsPerPageOptions"
                                        size="sm"
                                        style="width: 80px;"
                                        class="mr-3"
                                    ></b-form-select>
                                    <span class="text-muted">
                                        {{ (newClientsCurrentPage - 1) * newClientsPerPage + 1 }}-{{ Math.min(newClientsCurrentPage * newClientsPerPage, newClientsData.length) }} 
                                        из {{ newClientsData.length }}
                                    </span>
                                </div>
                                
                                <b-pagination
                                    v-model="newClientsCurrentPage"
                                    :total-rows="newClientsData.length"
                                    :per-page="newClientsPerPage"
                                    size="sm"
                                    class="mb-0"
                                ></b-pagination>
                            </div>

                            <!-- Итоги по новым клиентам -->
                            <b-alert variant="success" show class="py-2 mt-3">
                                <div class="d-flex justify-content-between align-items-center">
                                    <span class="font-weight-bold">Итоги по новым клиентам:</span>
                                    <span class="font-weight-bold">
                                        {{ newClientsSummary.clients_count }} клиентов, 
                                        {{ formatCurrency(newClientsSummary.total_margin) }} маржа, 
                                        {{ formatCurrency(newClientsSummary.total_bonus) }} бонус
                                    </span>
                                </div>
                            </b-alert>
                        </div>
                        
                        <!-- Сообщение если нет новых клиентов -->
                        <div v-else class="text-center py-4">
                            <i class="fas fa-user-plus fa-2x text-muted mb-2"></i>
                            <p class="text-muted mb-0">Нет новых клиентов за выбранный период</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Графики с табами -->
            <div class="charts-section mb-4">
                <div class="card bdr-tabs">
                    <div class="card-header">
                        <ul class="nav nav-tabs card-header-tabs" role="tablist">
                            <li class="nav-item">
                                <a class="nav-link" 
                                :class="{ active: activeChartTab === 'declarations' }"
                                @click="switchChartTab('declarations')"
                                href="#"
                                role="tab">
                                    <i class="fas fa-file-alt mr-2"></i>
                                    <template v-if="!hideMotivationText">Твои декларации</template>
                                    <template v-else>Декларации</template>
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" 
                                :class="{ active: activeChartTab === 'marginality' }"
                                @click="switchChartTab('marginality')"
                                href="#"
                                role="tab">
                                    <i class="fas fa-ruble-sign mr-2"></i>
                                    <template v-if="!hideMotivationText">Твоя маржинальность</template>
                                    <template v-else>Маржинальность</template>
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div class="card-body">
                        <!-- График деклараций -->
                        <div v-if="activeChartTab === 'declarations'" class="chart-container">
                            <MotivationDeclarationsChart 
                                :key="`declarations-${chartKey}`"
                                :rawChartData="declarationsChartData"
                                :loading="declarationsChartLoading"
                                title="Количество деклараций"
                            />
                        </div>

                        <!-- График маржинальности -->
                        <div v-if="activeChartTab === 'marginality'" class="chart-container">
                            <MotivationMarginalityChart 
                                :key="`marginality-${chartKey}`"
                                :rawChartData="marginalityChartData"
                                :loading="marginalityChartLoading"
                                title="Маржинальность (₽)"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Советы по увеличению дохода для сотрудников -->
            <div v-if="!hideMotivationText" class="tips-section mb-4">
                <div class="card">
                    <div class="card-header">
                        <h6 class="mb-0">Как увеличить доход</h6>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-4">
                                <div class="tip-item">
                                    <h6 class="text-primary">Увеличивайте объем</h6>
                                    <p>Стремитесь к плану в 90+ деклараций. Каждая следующая декларация сверх плана увеличивает вашу маржинальность.</p>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="tip-item">
                                    <h6 class="text-success">Увеличивайте маржинальность</h6>
                                    <p>Работайте над тем, чтобы каждая ваша сделка была максимально прибыльной для компании.</p>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="tip-item">
                                    <h6 class="text-info">Приводите новых клиентов</h6>
                                    <p>Каждый новый клиент — это мгновенный бонус в 20% от прибыли по его сделке.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- OrderList компонент -->
        <OrderList
            v-if="showOrderList"
            :openOrderList="showOrderList"
            :orderList="selectedOrders"
            :label="ordersModalTitle"
        />
    </div>
</template>

<script>
import { 
    API_DEPARTMENT_EFFICIENCY_DATA,
    API_MOTIVATION2025_TO_PERSONAL,
    // ТО API
    API_MOTIVATION2025_CHARTS_DECLARATIONS,
    API_MOTIVATION2025_CHARTS_TO_MARGINALITY,
    API_MOTIVATION2025_DETAILED_CALCULATIONS
} from '@/constants/api'

import MotivationDeclarationsChart from '@components/charts/MotivationDeclarationsChart.vue'
import MotivationMarginalityChart from '@components/charts/MotivationMarginalityChart.vue'
import OrderList from '@/components/orders/OrderList.vue'
import { openOrderSidebar } from '@/mixins/openOrderSidebar'

export default {
    name: 'TOPersonalStats',
    components: {
        MotivationDeclarationsChart,
        MotivationMarginalityChart,
        OrderList
    },
    mixins: [openOrderSidebar],
    props: {
        userId: {
            type: Number,
            required: true
        },
        period: {
            type: String,
            default: 'current'
        },
        hideMotivationText: {
            type: Boolean,
            default: false
        },
        openInNewWindow: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: false,
            activeChartTab: 'declarations',
            declarationsChartLoading: false,
            marginalityChartLoading: false,
            declarationsChartData: [],
            marginalityChartData: [],
            chartKey: 0, // Ключ для принудительного перерендера графиков
            currentPeriod: '', // Локальная переменная для периода
            personalStats: {
                declarations: 45,
                margin: 1200000,
                superIncome: 900000,
                bonus: 63000,
                bonusPercentage: 7,
                planCompletion: 50,
                planBonus: 63000,
                newClientsBonus: 0
            },
            // Данные для OrderList компонента
            selectedOrders: [],
            // Данные для списка деклараций
            declarationsData: [],
            declarationsLoading: false,
            declarationsSummary: {
                total_orders: 0,
                total_dt_count: 0
            },
            // Пагинация для таблицы деклараций
            declarationsCurrentPage: 1,
            declarationsPerPage: 10,
            declarationsPerPageOptions: [
                { value: 10, text: '10' },
                { value: 25, text: '25' },
                { value: 50, text: '50' },
                { value: 100, text: '100' }
            ],
            declarationsTableFields: [
                { key: 'order_id', label: 'ID поставки', sortable: true },
                { key: 'company_name', label: 'Наименование клиента', sortable: true },
                { key: 'dt_numbers', label: 'Выпущены ДТ', sortable: false },
                { key: 'dt_count', label: 'Кол-во', sortable: true }
            ],
            showOrderList: false,
            ordersModalTitle: '',
            // Данные для детальных расчетов маржинальности
            detailedCalculations: [],
            detailedCalculationsLoading: false,
            // Пагинация для таблицы детальных расчетов
            currentPage: 1,
            perPage: 10,
            perPageOptions: [
                { value: 10, text: '10' },
                { value: 25, text: '25' },
                { value: 50, text: '50' },
                { value: 100, text: '100' }
            ],
            // Поля для таблицы детальных расчетов
            detailedCalculationsFields: [
                {
                    key: 'order_id',
                    label: 'Заказ',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'company_name',
                    label: 'Наименование клиента',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'service_name',
                    label: 'Тип услуги',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'client_amount',
                    label: 'Сумма клиента',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'supplier_amount',
                    label: 'Сумма поставщика',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'margin',
                    label: 'Маржа',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'is_positive_margin',
                    label: 'Статус',
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-center'
                },
                {
                    key: 'calculated_at',
                    label: 'Дата расчета',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                }
            ],
            // Данные для новых клиентов
            newClientsData: [],
            newClientsLoading: false,
            newClientsSummary: {
                clients_count: 0,
                total_margin: 0,
                total_bonus: 0
            },
            // Пагинация для таблицы новых клиентов
            newClientsCurrentPage: 1,
            newClientsPerPage: 10,
            newClientsPerPageOptions: [
                { value: 10, text: '10' },
                { value: 25, text: '25' },
                { value: 50, text: '50' },
                { value: 100, text: '100' }
            ],
            // Поля для таблицы новых клиентов
            newClientsTableFields: [
                {
                    key: 'order_id',
                    label: 'Заказ',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'company_name',
                    label: 'Наименование клиента',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'service_name',
                    label: 'Тип услуги',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                },
                {
                    key: 'client_amount',
                    label: 'Сумма клиента',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'supplier_amount',
                    label: 'Сумма поставщика',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'margin',
                    label: 'Маржа',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'bonus',
                    label: 'Бонус (20%)',
                    sortable: true,
                    thClass: 'text-right',
                    tdClass: 'text-right'
                },
                {
                    key: 'is_positive_margin',
                    label: 'Статус',
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-center'
                },
                {
                    key: 'calculated_at',
                    label: 'Дата расчета',
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left'
                }
            ]
        }
    },
    mounted() {
        // Инициализируем квартал если не задан
        if (!this.period || this.period === 'current') {
            this.currentPeriod = this.getCurrentQuarter()
        } else {
            this.currentPeriod = this.period
        }
        
        // Загружаем данные при старте страницы
        this.loadPersonalStats()
        
        // Загружаем данные для графиков с небольшой задержкой
        this.$nextTick(() => {
            this.loadChartData()
        })
    },
    computed: {
        // Используем computed свойство для периода
        periodValue() {
            return this.currentPeriod || this.period
        }
    },
    watch: {
        // Сбрасываем пагинацию при изменении данных
        detailedCalculations() {
            this.currentPage = 1
        },
        // Сбрасываем пагинацию при изменении количества записей на странице
        perPage() {
            this.currentPage = 1
        },
        // Сбрасываем пагинацию деклараций при изменении данных
        declarationsData() {
            this.declarationsCurrentPage = 1
        },
        // Сбрасываем пагинацию деклараций при изменении количества записей на странице
        declarationsPerPage() {
            this.declarationsCurrentPage = 1
        },
        // Сбрасываем пагинацию новых клиентов при изменении данных
        newClientsData() {
            this.newClientsCurrentPage = 1
        },
        // Сбрасываем пагинацию новых клиентов при изменении количества записей на странице
        newClientsPerPage() {
            this.newClientsCurrentPage = 1
        }
    },
    methods: {
        formatCurrency(amount) {
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'RUB',
                minimumFractionDigits: 0
            }).format(amount)
        },
        formatDate(dateString) {
            const date = new Date(dateString)
            return date.toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },
        loadPersonalStats() {
            // Загружаем персональную статистику с сервера
            this.loadPersonalMotivationData()
            // Загружаем данные для графиков
            this.loadChartData()
        },
        loadPersonalMotivationData() {
            this.loading = true
            
            const params = {
                quarter: this.periodValue,
                department_id: 2, // Отдел ТО
                user_id: this.userId
            }
            
            axios.get(API_MOTIVATION2025_TO_PERSONAL, { params })
                .then(response => {
                    if (response.data.success && response.data.data.length > 0) {
                        this.processPersonalData(response.data.data[0])
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading personal motivation data:', error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        processPersonalData(data) {
            // Рассчитываем бонус по плану (общий бонус - бонус за новых клиентов - бонус руководителя)
            const newClientsBonus = data.new_clients?.bonus || 0
            const headDepartmentBonus = data.bonus_breakdown?.head_department_bonus || 0
            const planBonus = data.bonus_breakdown?.employee_bonus || 0

            // Обновляем персональную статистику
            this.personalStats = {
                declarations: data.declarations_count,
                margin: data.margin.total_margin,
                superIncome: data.super_income,
                bonus: data.bonus, // Общий бонус (включает все компоненты)
                bonusPercentage: data.bonus_percentage,
                planCompletion: Math.round((data.declarations_count / 90) * 100),
                planBonus: planBonus, // Только бонус за выполнение плана
                newClientsBonus: newClientsBonus,
                headDepartmentBonus: headDepartmentBonus,
                head_department_info: data.head_department_info || { is_head_manager: false }
            }
            
            // Обновляем детальные данные о декларациях
            if (data.declarations_data && Array.isArray(data.declarations_data)) {
                this.declarationsData = data.declarations_data
                this.declarationsSummary = {
                    total_orders: data.declarations_summary?.total_orders || 0,
                    total_dt_count: data.declarations_summary?.total_dt_count || 0
                }
            }
            
            // Обновляем детальные расчеты маржинальности
            if (data.margin && data.margin.detailed_calculations && Array.isArray(data.margin.detailed_calculations)) {
                this.detailedCalculations = data.margin.detailed_calculations
            } else {
                this.detailedCalculations = []
            }

            // Обновляем данные о новых клиентах
            if (data.new_clients && data.new_clients.detailed_calculations && Array.isArray(data.new_clients.detailed_calculations)) {
                // Добавляем бонус к каждой записи
                this.newClientsData = data.new_clients.detailed_calculations.map(item => ({
                    ...item,
                    bonus: item.margin * 0.20 // 20% от маржи
                }))
                
                this.newClientsSummary = {
                    clients_count: data.new_clients.clients_count || 0,
                    total_margin: data.new_clients.total_margin || 0,
                    total_bonus: data.new_clients.bonus || 0
                }
            } else {
                this.newClientsData = []
                this.newClientsSummary = {
                    clients_count: 0,
                    total_margin: 0,
                    total_bonus: 0
                }
            }
        },
        getCurrentQuarter() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            const minYear = 2025
            const minQuarter = 4 // Q4 2025 - минимальный квартал
            
            let currentQuarter = ''
            
            // Финансовая схема кварталов (год начинается с декабря):
            // Q1: декабрь, январь, февраль (12,1,2)
            // Q2: март, апрель, май (3,4,5)
            // Q3: июнь, июль, август (6,7,8)
            // Q4: сентябрь, октябрь, ноябрь (9,10,11)
            if (month === 12 || month === 1 || month === 2) {
                // Для декабря используем следующий год
                currentQuarter = month === 12 ? `${year + 1}-Q1` : `${year}-Q1`
            } else if (month >= 3 && month <= 5) {
                currentQuarter = `${year}-Q2`
            } else if (month >= 6 && month <= 8) {
                currentQuarter = `${year}-Q3`
            } else if (month >= 9 && month <= 11) {
                currentQuarter = `${year}-Q4`
            }
            
            // Проверяем, что квартал не раньше Q4 2025
            const [qYear, qNum] = currentQuarter.split('-Q')
            const quarterNum = parseInt(qNum)
            
            if (qYear < minYear || (qYear == minYear && quarterNum < minQuarter)) {
                return '2025-Q4' // Возвращаем минимальный доступный квартал
            }
            
            return currentQuarter || '2025-Q4'
        },
        switchChartTab(tab) {
            this.activeChartTab = tab
        },
        loadChartData() {
            this.loadDeclarationsChartData()
            this.loadMarginalityChartData()
        },
        loadDeclarationsChartData() {
            this.declarationsChartLoading = true
            
            const params = {
                quarter: this.periodValue,
                department_id: 2,
                user_id: this.userId
            }
            
            axios.get(API_MOTIVATION2025_CHARTS_DECLARATIONS, { params })
                .then(response => {
                    if (response.data.success) {
                        this.declarationsChartData = response.data.data
                        // Обновляем ключ после успешной загрузки данных
                        this.$nextTick(() => {
                            this.chartKey++
                            console.log('🔄 Personal declarations chart key updated to:', this.chartKey)
                        })
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading declarations chart data:', error)
                })
                .finally(() => {
                    this.declarationsChartLoading = false
                })
        },
        loadMarginalityChartData() {
            this.marginalityChartLoading = true
            
            const params = {
                quarter: this.periodValue,
                department_id: 2,
                user_id: this.userId
            }
            
            axios.get(API_MOTIVATION2025_CHARTS_TO_MARGINALITY, { params })
                .then(response => {
                    if (response.data.success) {
                        this.marginalityChartData = response.data.data
                        // Обновляем ключ после успешной загрузки данных
                        this.$nextTick(() => {
                            this.chartKey++
                            console.log('🔄 Personal marginality chart key updated to:', this.chartKey)
                        })
                    } else {
                        console.error('API Error:', response.data.message)
                    }
                })
                .catch(error => {
                    console.error('Error loading marginality chart data:', error)
                })
                .finally(() => {
                    this.marginalityChartLoading = false
                })
        },
        showDeclarationsModal(type) {
            // Получаем данные о декларациях из API
            this.loadDeclarationsData(type)
        },
        loadDeclarationsData(type) {
            const params = {
                quarter: this.periodValue,
                department_id: 2,
                user_id: this.userId
            }
            
            axios.get(API_DEPARTMENT_EFFICIENCY_DATA, { params })
                .then(response => {
                    if (response.data.success && response.data.data.length > 0) {
                        // Находим данные текущего пользователя
                        const userData = response.data.data.find(user => user.employee_id === this.userId)
                        if (userData) {
                            let orders = []
                            let title = ''
                            
                            switch (type) {
                                case 'completed':
                                    orders = userData.completed_declarations || []
                                    title = `Оформленные декларации - ${userData.employee_name}`
                                    break
                            }
                            
                            if (orders.length === 0) {
                                this.$bvToast.toast('Декларации не найдены', {
                                    title: 'Информация',
                                    variant: 'info',
                                    solid: true
                                })
                                return
                            }
                            
                            this.selectedOrders = orders
                            this.ordersModalTitle = title
                            this.showOrderList = true
                        }
                    } else {
                        this.$bvToast.toast('Данные не найдены', {
                            title: 'Ошибка',
                            variant: 'warning',
                            solid: true
                        })
                    }
                })
                .catch(error => {
                    console.error('Error loading declarations data:', error)
                    this.$bvToast.toast('Ошибка загрузки данных', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    })
                })
        },
        resetOrdersModal() {
            this.selectedOrders = []
            this.showOrderList = false
            this.ordersModalTitle = ''
        },
        loadDetailedCalculations() {
            this.detailedCalculationsLoading = true
            
            // Загружаем детальные расчеты напрямую из API
            this.loadDetailedCalculationsFromAPI()
        },
        async loadDetailedCalculationsFromAPI() {
            try {
                const response = await axios.get(API_MOTIVATION2025_DETAILED_CALCULATIONS, {
                    params: {
                        quarter: this.periodValue,
                        user_id: this.userId
                    }
                })
                
                if (response.data.success) {
                    this.detailedCalculations = response.data.data || []
                } else {
                    console.error('Ошибка API:', response.data.message)
                    this.detailedCalculations = []
                }
            } catch (error) {
                console.error('Ошибка при загрузке детальных расчетов:', error)
                this.detailedCalculations = []
            } finally {
                this.detailedCalculationsLoading = false
            }
        },
        openOrderInNewWindow(orderId) {
            // Открываем заказ в новом окне/вкладке
            const routeData = this.$router.resolve({
                path: '/orders',
                query: { order: orderId }
            })
            window.open(routeData.href, '_blank')
        },
        
        getCurrentQuarterName() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            
            // Финансовая схема кварталов (год начинается с декабря):
            // Q1: декабрь, январь, февраль (12,1,2)
            // Q2: март, апрель, май (3,4,5)
            // Q3: июнь, июль, август (6,7,8)
            // Q4: сентябрь, октябрь, ноябрь (9,10,11)
            if (month === 12 || month === 1 || month === 2) {
                const displayYear = month === 12 ? year + 1 : year
                return `Q1 ${displayYear} (декабрь, январь, февраль)`
            }
            if (month >= 3 && month <= 5) return `Q2 ${year} (март, апрель, май)`
            if (month >= 6 && month <= 8) return `Q3 ${year} (июнь, июль, август)`
            if (month >= 9 && month <= 11) return `Q4 ${year} (сентябрь, октябрь, ноябрь)`
            
            return `Q4 ${year} (сентябрь, октябрь, ноябрь)`
        },
        
        getCurrentQuarterDates() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            
            let startDate, endDate
            
            // Финансовая схема кварталов (год начинается с декабря):
            // Q1: декабрь, январь, февраль (12,1,2)
            // Q2: март, апрель, май (3,4,5)
            // Q3: июнь, июль, август (6,7,8)
            // Q4: сентябрь, октябрь, ноябрь (9,10,11)
            if (month === 12 || month === 1 || month === 2) {
                // Q1: декабрь предыдущего года, январь и февраль текущего года
                if (month === 12) {
                    startDate = new Date(year, 11, 1) // декабрь текущего года
                    endDate = new Date(year + 1, 1, 28) // февраль следующего года (28 для високосных)
                } else {
                    startDate = new Date(year - 1, 11, 1) // декабрь предыдущего года
                    endDate = new Date(year, 1, 28) // февраль текущего года
                }
            } else if (month >= 3 && month <= 5) {
                // Q2: март, апрель, май
                startDate = new Date(year, 2, 1) // март
                endDate = new Date(year, 4, 31) // май
            } else if (month >= 6 && month <= 8) {
                // Q3: июнь, июль, август
                startDate = new Date(year, 5, 1) // июнь
                endDate = new Date(year, 7, 31) // август
            } else if (month >= 9 && month <= 11) {
                // Q4: сентябрь, октябрь, ноябрь
                startDate = new Date(year, 8, 1) // сентябрь
                endDate = new Date(year, 10, 30) // ноябрь
            }
            
            return {
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0]
            }
        }
    }
}
</script>

<style scoped>
.metric-card {
    display: flex;
    align-items: center;
    padding: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    background-color: #f8f9fa;
    height: 100%;
}

.metric-icon {
    font-size: 2rem;
    margin-right: 1rem;
}

.metric-content {
    flex: 1;
}

.metric-value {
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 0.25rem;
}

.metric-label {
    font-size: 0.875rem;
    color: #6c757d;
}

.stat-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f8f9fa;
}

.stat-item:last-child {
    border-bottom: none;
}

.stat-label {
    font-weight: 500;
    color: #495057;
}

.stat-value {
    font-weight: bold;
}

.table th {
    border-top: none;
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
}

.table td {
    font-size: 0.875rem;
}

/* Chart styles */
.chart-container {
    position: relative;
    height: 400px;
}

.chart-wrapper {
    position: relative;
    height: 100%;
}

.nav-tabs .nav-link {
    border: none;
    border-bottom: 2px solid transparent;
    color: #6c757d;
    font-weight: 500;
}

.nav-tabs .nav-link:hover {
    border-color: transparent;
    border-bottom-color: #dee2e6;
    color: #495057;
}

.nav-tabs .nav-link.active {
    color: #0D6247;
    border-bottom-color: #0D6247;
    background-color: transparent;
}

.nav-tabs .nav-link i {
    font-size: 0.875rem;
}

/* Стили для персональной мотивации */
.motivation-header .card {
    border-left: 4px solid #0D6247;
}

.key-metrics .card {
    border: none;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.metric-value {
    margin-top: 1rem;
}

.bonus-table .table th {
    background-color: #f8f9fa;
    border-top: none;
}

.calculation-step {
    text-align: center;
    padding: 1rem;
}

.calculation-result {
    margin-top: 0.5rem;
}

.final-bonus {
    background-color: #f8f9fa;
    padding: 1.5rem;
    border-radius: 0.5rem;
    margin-top: 1rem;
}

.tip-item {
    padding: 1rem;
    border-left: 3px solid #0D6247;
    margin-bottom: 1rem;
}

/* Стили для таблицы расчета эффективности */
.efficiency-calculation-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
}

.efficiency-calculation-table .table td {
    vertical-align: middle;
}

.efficiency-calculation-table .badge {
    font-size: 0.75rem;
    margin-right: 0.25rem;
}

.efficiency-calculation-table .table-success td {
    background-color: #d4edda !important;
    border-color: #c3e6cb !important;
}

.efficiency-calculation-table .table-info td {
    background-color: #d1ecf1 !important;
    border-color: #bee5eb !important;
}

/* Стили для кликабельных цифр */
.clickable-number {
    cursor: pointer;
    text-decoration: underline;
    transition: all 0.1s ease;
}

.clickable-number:hover {
    color: #0056b3 !important;
    text-decoration: none;
    font-weight: bold;
}

/* Жирное выделение для ссылок с заказами */
.clickable-number.has-orders {
    font-weight: bold;
    font-size: 1.05rem;
}

.clickable-number.has-orders:hover {
    font-weight: 900;
}

/* Стили для таблицы расчета маржинальности */
.marginality-calculation-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
}

.marginality-calculation-table .table td {
    vertical-align: middle;
}

.marginality-calculation-table .badge {
    font-size: 0.75rem;
}

/* Стили для кликабельных ссылок в таблице маржинальности */
.sidebar-link {
    cursor: pointer;
    text-decoration: underline;
    transition: all 0.1s ease;
}

.sidebar-link:hover {
    color: #0056b3 !important;
    text-decoration: none;
    font-weight: bold;
}

.cursor-pointer {
    cursor: pointer;
}

/* Стили для таблицы детальных расчетов с пагинацией */
.detailed-calculations-table {
    margin-bottom: 0;
}

.detailed-calculations-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
}

.detailed-calculations-table .table td {
    vertical-align: middle;
    font-size: 0.875rem;
}

.detailed-calculations-table .badge {
    font-size: 0.75rem;
}

/* Стили для пагинации */
.pagination {
    margin-bottom: 0;
}

.pagination .page-link {
    color: #0D6247;
    border-color: #dee2e6;
}

.pagination .page-link:hover {
    color: #0D6247;
    background-color: #e9ecef;
    border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
    background-color: #0D6247;
    border-color: #0D6247;
}

.pagination .page-item.disabled .page-link {
    color: #6c757d;
    background-color: #fff;
    border-color: #dee2e6;
}

/* Стили для таблицы новых клиентов */
.new-clients-table .table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
}

.new-clients-table .table td {
    vertical-align: middle;
    font-size: 0.875rem;
}

.new-clients-table .badge {
    font-size: 0.75rem;
}

/* Стили для разбивки бонуса */
.bonus-breakdown {
    text-align: left;
    margin-top: 1rem;
}

.bonus-breakdown p {
    margin-bottom: 0.5rem;
}
</style>
